'use client';

import { c as _c } from "react/compiler-runtime";
import type { CustomerFunnelStage, MarketingActivationFormat, MarketingChannel, MarketingCollaborationModel, MarketingDeliveryActivity, MarketingAttributes, MarketingObjective, MarketingRole, MarketingTactic, ProductStage } from '@sizzlenz/shared';
import { CUSTOMER_FUNNEL_STAGES, MARKETING_ACTIVATION_FORMATS, MARKETING_CHANNELS, MARKETING_COLLABORATION_MODELS, MARKETING_DELIVERY_ACTIVITIES, MARKETING_DELIVERY_DISCIPLINES, MARKETING_OBJECTIVES, MARKETING_ROLES, MARKETING_TACTICS, PRODUCT_STAGES } from '@sizzlenz/shared';
import { ChevronDown, FlaskConical, Goal, Handshake, Lightbulb, Lock, Radio, Route, SlidersHorizontal, Store, Target, Wrench, X, type LucideIcon } from 'lucide-react';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { Field, FieldGroup, FieldLabel, FieldLegend, FieldSet } from '@/components/ui/field';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
export interface InspirationFilterValue {
  demandRoles: MarketingRole[];
  journeyStages: CustomerFunnelStage[];
  productStages: ProductStage[];
  demandObjectives: MarketingObjective[];
  tactics: MarketingTactic[];
  channels: MarketingChannel[];
  deliveryActivities: MarketingDeliveryActivity[];
  collaborationModels: MarketingCollaborationModel[];
  activationFormats: MarketingActivationFormat[];
}
export const createEmptyInspirationFilters = (): InspirationFilterValue => ({
  demandRoles: [],
  journeyStages: [],
  productStages: [],
  demandObjectives: [],
  tactics: [],
  channels: [],
  deliveryActivities: [],
  collaborationModels: [],
  activationFormats: []
});
export type InspirationFilterKey = keyof InspirationFilterValue;
interface FilterOption {
  id: string;
  label: string;
  groupLabel?: string;
}
interface FilterFacet {
  key: InspirationFilterKey;
  label: string;
  mobileLabel: string;
  icon: LucideIcon;
  options: FilterOption[];
}
const deliveryDisciplineLabels = new Map(MARKETING_DELIVERY_DISCIPLINES.map(discipline => [discipline.id, discipline.label]));
const FILTER_FACETS: FilterFacet[] = [{
  key: 'demandRoles',
  label: 'Demand role',
  mobileLabel: 'What role does it play?',
  icon: Target,
  options: MARKETING_ROLES.map(({
    id,
    label
  }) => ({
    id,
    label
  }))
}, {
  key: 'journeyStages',
  label: 'Journey stage',
  mobileLabel: 'Where is the consumer?',
  icon: Route,
  options: CUSTOMER_FUNNEL_STAGES.map(({
    id,
    label
  }) => ({
    id,
    label
  }))
}, {
  key: 'productStages',
  label: 'Product stage',
  mobileLabel: 'Where is the product?',
  icon: FlaskConical,
  options: PRODUCT_STAGES.map(({
    id,
    label
  }) => ({
    id,
    label
  }))
}, {
  key: 'demandObjectives',
  label: 'Objective',
  mobileLabel: 'What needs to change?',
  icon: Goal,
  options: MARKETING_OBJECTIVES.map(({
    id,
    label
  }) => ({
    id,
    label
  }))
}, {
  key: 'tactics',
  label: 'Tactic',
  mobileLabel: 'Which approach is being used?',
  icon: Lightbulb,
  options: MARKETING_TACTICS.map(({
    id,
    label
  }) => ({
    id,
    label
  }))
}, {
  key: 'channels',
  label: 'Channel',
  mobileLabel: 'Where does it happen?',
  icon: Radio,
  options: MARKETING_CHANNELS.map(({
    id,
    label
  }) => ({
    id,
    label
  }))
}, {
  key: 'deliveryActivities',
  label: 'Execution',
  mobileLabel: 'What is needed to execute?',
  icon: Wrench,
  options: MARKETING_DELIVERY_ACTIVITIES.map(({
    id,
    label,
    disciplineId
  }) => ({
    id,
    label,
    groupLabel: deliveryDisciplineLabels.get(disciplineId)
  }))
}, {
  key: 'collaborationModels',
  label: 'Collaboration',
  mobileLabel: 'How did brands work together?',
  icon: Handshake,
  options: MARKETING_COLLABORATION_MODELS.map(({
    id,
    label
  }) => ({
    id,
    label
  }))
}, {
  key: 'activationFormats',
  label: 'Activation format',
  mobileLabel: 'What physical form did it take?',
  icon: Store,
  options: MARKETING_ACTIVATION_FORMATS.map(({
    id,
    label
  }) => ({
    id,
    label
  }))
}];
export const INSPIRATION_FILTER_KEYS = FILTER_FACETS.map(facet => facet.key) as InspirationFilterKey[];
const FACET_ICONS = new Map<InspirationFilterKey, LucideIcon>(FILTER_FACETS.map(facet => [facet.key, facet.icon]));
const selectionsMatch = (selected: readonly string[], available: readonly string[]): boolean => selected.length === 0 || selected.some(value => available.includes(value));
export function matchesInspirationFilters(attributes: MarketingAttributes, filters: InspirationFilterValue, visibleFilters: readonly InspirationFilterKey[] = INSPIRATION_FILTER_KEYS): boolean {
  const isVisible = (key: InspirationFilterKey) => visibleFilters.includes(key);
  return (!isVisible('demandRoles') || selectionsMatch(filters.demandRoles, attributes.marketingRoles)) && (!isVisible('journeyStages') || selectionsMatch(filters.journeyStages, attributes.journeyStages)) && (!isVisible('productStages') || selectionsMatch(filters.productStages, attributes.productStages ?? [])) && (!isVisible('demandObjectives') || selectionsMatch(filters.demandObjectives, attributes.marketingObjectives)) && (!isVisible('tactics') || selectionsMatch(filters.tactics, attributes.tactics)) && (!isVisible('channels') || selectionsMatch(filters.channels, attributes.channels)) && (!isVisible('deliveryActivities') || selectionsMatch(filters.deliveryActivities, attributes.deliveryActivities)) && (!isVisible('collaborationModels') || selectionsMatch(filters.collaborationModels, attributes.collaborationModels ?? [])) && (!isVisible('activationFormats') || selectionsMatch(filters.activationFormats, attributes.activationFormats ?? []));
}
interface InspirationFiltersProps {
  value: InspirationFilterValue;
  onChange: (value: InspirationFilterValue) => void;
  resultCount: number;
  totalCount: number;
  visibleFilters?: readonly InspirationFilterKey[];
  /**
   * Facet values that are fixed by the host and cannot be toggled off or cleared
   * by the user. They render as checked + disabled in the option lists and as
   * non-removable chips. The facet control stays visible so the user can still
   * add other values on top (OR within the facet).
   */
  lockedSelections?: Partial<Record<InspirationFilterKey, readonly string[]>>;
  /**
   * Controls whether locked selections are surfaced in the UI. When `true`
   * (default), locked values render as checked + disabled options and as
   * non-removable `Lock` chips. When `false`, locked values still apply to the
   * query (the host owns them) but are hidden from the option lists and the
   * active-selection chips, so the user never sees them.
   */
  showLockedFilters?: boolean;
  className?: string;
  /**
   * Optional trailing controls (e.g. sort selector + layout toggle). On desktop
   * they sit on the right of the facet row. On mobile they render on their own
   * row directly beneath the filter drawer trigger, outside the drawer.
   */
  trailing?: ReactNode;
  /**
   * Optional quick-filter pills rendered inside the filters card, above the
   * facet controls. Lets a host integrate the quick filters with the detailed
   * facets in a single card instead of a separate row. Omit to render nothing.
   */
  quickFilters?: ReactNode;
}
export function InspirationFilters(t0) {
  const $ = _c(132);
  const {
    value,
    onChange,
    resultCount,
    visibleFilters: t1,
    lockedSelections,
    showLockedFilters: t2,
    className,
    trailing,
    quickFilters
  } = t0;
  const visibleFilters = t1 === undefined ? INSPIRATION_FILTER_KEYS : t1;
  const showLockedFilters = t2 === undefined ? true : t2;
  const [mobileOpen, setMobileOpen] = useState(false);
  let t3;
  if ($[0] !== visibleFilters) {
    t3 = FILTER_FACETS.filter(facet => visibleFilters.includes(facet.key));
    $[0] = visibleFilters;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const visibleFacets = t3;
  let t4;
  if ($[2] !== lockedSelections) {
    t4 = (key, id) => (lockedSelections?.[key] ?? []).includes(id);
    $[2] = lockedSelections;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const isLocked = t4;
  let t5;
  if ($[4] !== isLocked || $[5] !== showLockedFilters) {
    t5 = (key_0, id_0) => !showLockedFilters && isLocked(key_0, id_0);
    $[4] = isLocked;
    $[5] = showLockedFilters;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const isHiddenLocked = t5;
  let t6;
  if ($[7] !== lockedSelections || $[8] !== showLockedFilters) {
    t6 = key_1 => showLockedFilters ? lockedSelections?.[key_1] ?? [] : [];
    $[7] = lockedSelections;
    $[8] = showLockedFilters;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const displayLockedFor = t6;
  let t7;
  if ($[10] !== lockedSelections || $[11] !== showLockedFilters) {
    t7 = key_2 => showLockedFilters ? [] : lockedSelections?.[key_2] ?? [];
    $[10] = lockedSelections;
    $[11] = showLockedFilters;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const hiddenIdsFor = t7;
  let t8;
  if ($[13] !== isLocked || $[14] !== value) {
    t8 = key_3 => (value[key_3] as string[]).some(id_1 => !isLocked(key_3, id_1));
    $[13] = isLocked;
    $[14] = value;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const facetHasClearableSelection = t8;
  let t9;
  if ($[16] !== isHiddenLocked || $[17] !== value) {
    t9 = key_4 => (value[key_4] as string[]).filter(id_2 => !isHiddenLocked(key_4, id_2));
    $[16] = isHiddenLocked;
    $[17] = value;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const displaySelectedIds = t9;
  let t10;
  if ($[19] !== displaySelectedIds) {
    t10 = (count, facet_0) => count + displaySelectedIds(facet_0.key).length;
    $[19] = displaySelectedIds;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const activeFilterCount = visibleFacets.reduce(t10, 0);
  const hasActiveFilters = activeFilterCount > 0;
  let t11;
  if ($[21] !== facetHasClearableSelection || $[22] !== visibleFacets) {
    let t12;
    if ($[24] !== facetHasClearableSelection) {
      t12 = facet_1 => facetHasClearableSelection(facet_1.key);
      $[24] = facetHasClearableSelection;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    t11 = visibleFacets.some(t12);
    $[21] = facetHasClearableSelection;
    $[22] = visibleFacets;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const hasClearableFilters = t11;
  let t12;
  if ($[26] !== isHiddenLocked || $[27] !== isLocked || $[28] !== value || $[29] !== visibleFacets) {
    let t13;
    if ($[31] !== isHiddenLocked || $[32] !== isLocked || $[33] !== value) {
      t13 = facet_2 => facet_2.options.filter(option => (value[facet_2.key] as string[]).includes(option.id) && !isHiddenLocked(facet_2.key, option.id)).map(option_0 => ({
        facetKey: facet_2.key,
        option: option_0,
        locked: isLocked(facet_2.key, option_0.id)
      }));
      $[31] = isHiddenLocked;
      $[32] = isLocked;
      $[33] = value;
      $[34] = t13;
    } else {
      t13 = $[34];
    }
    t12 = visibleFacets.flatMap(t13);
    $[26] = isHiddenLocked;
    $[27] = isLocked;
    $[28] = value;
    $[29] = visibleFacets;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const activeSelections = t12;
  let t13;
  if ($[35] !== isLocked || $[36] !== onChange || $[37] !== value) {
    t13 = (key_5, id_3) => {
      if (isLocked(key_5, id_3)) {
        return;
      }
      const selected = value[key_5] as string[];
      const next = selected.includes(id_3) ? selected.filter(selectedId => selectedId !== id_3) : [...selected, id_3];
      onChange({
        ...value,
        [key_5]: next
      } as InspirationFilterValue);
    };
    $[35] = isLocked;
    $[36] = onChange;
    $[37] = value;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const toggleSelection = t13;
  let t14;
  if ($[39] !== isLocked || $[40] !== onChange || $[41] !== value) {
    t14 = key_6 => {
      const locked = (value[key_6] as string[]).filter(id_4 => isLocked(key_6, id_4));
      onChange({
        ...value,
        [key_6]: locked
      } as InspirationFilterValue);
    };
    $[39] = isLocked;
    $[40] = onChange;
    $[41] = value;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const clearFacet = t14;
  let t15;
  if ($[43] !== lockedSelections || $[44] !== onChange) {
    t15 = () => {
      const cleared = createEmptyInspirationFilters();
      if (lockedSelections) {
        const writable = cleared as unknown as Record<string, string[]>;
        for (const key_7 of Object.keys(lockedSelections) as InspirationFilterKey[]) {
          const locked_0 = lockedSelections[key_7];
          if (locked_0 && locked_0.length > 0) {
            writable[key_7] = [...locked_0];
          }
        }
      }
      onChange(cleared);
    };
    $[43] = lockedSelections;
    $[44] = onChange;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  const clearAll = t15;
  let t16;
  if ($[46] !== className) {
    t16 = cn("rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5", className);
    $[46] = className;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== quickFilters) {
    t17 = quickFilters && <div className="mb-4 border-b border-gray-100 pb-4">{quickFilters}</div>;
    $[48] = quickFilters;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== clearFacet || $[51] !== displayLockedFor || $[52] !== displaySelectedIds || $[53] !== facetHasClearableSelection || $[54] !== hiddenIdsFor || $[55] !== toggleSelection || $[56] !== visibleFacets) {
    let t19;
    if ($[58] !== clearFacet || $[59] !== displayLockedFor || $[60] !== displaySelectedIds || $[61] !== facetHasClearableSelection || $[62] !== hiddenIdsFor || $[63] !== toggleSelection) {
      t19 = facet_3 => <FacetPopover key={facet_3.key} facet={facet_3} selectedIds={displaySelectedIds(facet_3.key)} lockedIds={displayLockedFor(facet_3.key)} hiddenIds={hiddenIdsFor(facet_3.key)} hasClearableSelection={facetHasClearableSelection(facet_3.key)} onToggle={id_5 => toggleSelection(facet_3.key, id_5)} onClear={() => clearFacet(facet_3.key)} />;
      $[58] = clearFacet;
      $[59] = displayLockedFor;
      $[60] = displaySelectedIds;
      $[61] = facetHasClearableSelection;
      $[62] = hiddenIdsFor;
      $[63] = toggleSelection;
      $[64] = t19;
    } else {
      t19 = $[64];
    }
    t18 = visibleFacets.map(t19);
    $[50] = clearFacet;
    $[51] = displayLockedFor;
    $[52] = displaySelectedIds;
    $[53] = facetHasClearableSelection;
    $[54] = hiddenIdsFor;
    $[55] = toggleSelection;
    $[56] = visibleFacets;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[65] !== t18) {
    t19 = <div className="flex flex-wrap gap-2">{t18}</div>;
    $[65] = t18;
    $[66] = t19;
  } else {
    t19 = $[66];
  }
  let t20;
  if ($[67] !== trailing) {
    t20 = trailing && <div className="flex items-center justify-end gap-2">{trailing}</div>;
    $[67] = trailing;
    $[68] = t20;
  } else {
    t20 = $[68];
  }
  let t21;
  if ($[69] !== t19 || $[70] !== t20) {
    t21 = <div className="mt-4 hidden flex-col gap-3 md:flex">{t19}{t20}</div>;
    $[69] = t19;
    $[70] = t20;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  const t22 = hasActiveFilters ? "secondary" : "outline";
  let t23;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <span className="inline-flex items-center gap-2"><SlidersHorizontal data-icon="inline-start" />Filter</span>;
    $[72] = t23;
  } else {
    t23 = $[72];
  }
  let t24;
  if ($[73] !== activeFilterCount || $[74] !== hasActiveFilters) {
    t24 = hasActiveFilters ? <Badge variant="default">{activeFilterCount}</Badge> : <ChevronDown data-icon="inline-end" />;
    $[73] = activeFilterCount;
    $[74] = hasActiveFilters;
    $[75] = t24;
  } else {
    t24 = $[75];
  }
  let t25;
  if ($[76] !== t22 || $[77] !== t24) {
    t25 = <DrawerTrigger asChild={true}><Button type="button" variant={t22} size="lg" className="w-full cursor-pointer justify-between rounded-xl">{t23}{t24}</Button></DrawerTrigger>;
    $[76] = t22;
    $[77] = t24;
    $[78] = t25;
  } else {
    t25 = $[78];
  }
  let t26;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <DrawerHeader className="text-left"><DrawerTitle>Filter inspiration</DrawerTitle><DrawerDescription>Select one or more options. Choices within a group broaden the results.</DrawerDescription></DrawerHeader>;
    $[79] = t26;
  } else {
    t26 = $[79];
  }
  let t27;
  if ($[80] !== displayLockedFor || $[81] !== displaySelectedIds || $[82] !== hiddenIdsFor || $[83] !== toggleSelection || $[84] !== visibleFacets) {
    let t28;
    if ($[86] !== displayLockedFor || $[87] !== displaySelectedIds || $[88] !== hiddenIdsFor || $[89] !== toggleSelection) {
      t28 = facet_4 => <FilterOptionList key={facet_4.key} facet={facet_4} selectedIds={displaySelectedIds(facet_4.key)} lockedIds={displayLockedFor(facet_4.key)} hiddenIds={hiddenIdsFor(facet_4.key)} idPrefix="mobile" showLegend={true} onToggle={id_6 => toggleSelection(facet_4.key, id_6)} />;
      $[86] = displayLockedFor;
      $[87] = displaySelectedIds;
      $[88] = hiddenIdsFor;
      $[89] = toggleSelection;
      $[90] = t28;
    } else {
      t28 = $[90];
    }
    t27 = visibleFacets.map(t28);
    $[80] = displayLockedFor;
    $[81] = displaySelectedIds;
    $[82] = hiddenIdsFor;
    $[83] = toggleSelection;
    $[84] = visibleFacets;
    $[85] = t27;
  } else {
    t27 = $[85];
  }
  let t28;
  if ($[91] !== t27) {
    t28 = <ScrollArea className="h-[55vh] px-4"><div className="flex flex-col gap-7 pb-6">{t27}</div></ScrollArea>;
    $[91] = t27;
    $[92] = t28;
  } else {
    t28 = $[92];
  }
  let t29;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => setMobileOpen(false);
    $[93] = t29;
  } else {
    t29 = $[93];
  }
  const t30 = resultCount === 1 ? "example" : "examples";
  let t31;
  if ($[94] !== resultCount || $[95] !== t30) {
    t31 = <Button type="button" className="cursor-pointer" onClick={t29}>Show {resultCount} {t30}</Button>;
    $[94] = resultCount;
    $[95] = t30;
    $[96] = t31;
  } else {
    t31 = $[96];
  }
  const t32 = !hasClearableFilters;
  let t33;
  if ($[97] !== clearAll || $[98] !== t32) {
    t33 = <Button type="button" variant="ghost" className="cursor-pointer" disabled={t32} onClick={clearAll}>Clear all filters</Button>;
    $[97] = clearAll;
    $[98] = t32;
    $[99] = t33;
  } else {
    t33 = $[99];
  }
  let t34;
  if ($[100] !== t31 || $[101] !== t33) {
    t34 = <DrawerFooter className="border-t border-gray-200">{t31}{t33}</DrawerFooter>;
    $[100] = t31;
    $[101] = t33;
    $[102] = t34;
  } else {
    t34 = $[102];
  }
  let t35;
  if ($[103] !== t28 || $[104] !== t34) {
    t35 = <DrawerContent>{t26}{t28}{t34}</DrawerContent>;
    $[103] = t28;
    $[104] = t34;
    $[105] = t35;
  } else {
    t35 = $[105];
  }
  let t36;
  if ($[106] !== mobileOpen || $[107] !== t25 || $[108] !== t35) {
    t36 = <Drawer open={mobileOpen} onOpenChange={setMobileOpen}>{t25}{t35}</Drawer>;
    $[106] = mobileOpen;
    $[107] = t25;
    $[108] = t35;
    $[109] = t36;
  } else {
    t36 = $[109];
  }
  let t37;
  if ($[110] !== trailing) {
    t37 = trailing && <div className="mt-3 flex flex-wrap items-center gap-2">{trailing}</div>;
    $[110] = trailing;
    $[111] = t37;
  } else {
    t37 = $[111];
  }
  let t38;
  if ($[112] !== t36 || $[113] !== t37) {
    t38 = <div className="mt-4 md:hidden">{t36}{t37}</div>;
    $[112] = t36;
    $[113] = t37;
    $[114] = t38;
  } else {
    t38 = $[114];
  }
  let t39;
  if ($[115] !== activeSelections || $[116] !== toggleSelection) {
    let t40;
    if ($[118] !== toggleSelection) {
      t40 = t41 => {
        const {
          facetKey,
          option: option_1,
          locked: locked_1
        } = t41;
        const FacetIcon = FACET_ICONS.get(facetKey);
        return <Badge key={`${facetKey}-${option_1.id}`} variant="secondary" className={cn("gap-1 pl-2", locked_1 && "pr-2")}>{locked_1 ? <Lock className="size-3 shrink-0 text-gray-500" aria-hidden="true" /> : FacetIcon && <FacetIcon className="size-3 shrink-0 text-gray-500" aria-hidden="true" />}{option_1.label}{!locked_1 && <button type="button" className="cursor-pointer rounded-full p-0.5 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400" aria-label={`Remove ${option_1.label} filter`} onClick={() => toggleSelection(facetKey, option_1.id)}><X className="size-3" aria-hidden="true" /></button>}</Badge>;
      };
      $[118] = toggleSelection;
      $[119] = t40;
    } else {
      t40 = $[119];
    }
    t39 = activeSelections.map(t40);
    $[115] = activeSelections;
    $[116] = toggleSelection;
    $[117] = t39;
  } else {
    t39 = $[117];
  }
  let t40;
  if ($[120] !== clearAll || $[121] !== hasClearableFilters) {
    t40 = hasClearableFilters && <Button type="button" variant="ghost" size="sm" className="cursor-pointer text-gray-500" onClick={clearAll}>Clear all</Button>;
    $[120] = clearAll;
    $[121] = hasClearableFilters;
    $[122] = t40;
  } else {
    t40 = $[122];
  }
  let t41;
  if ($[123] !== t39 || $[124] !== t40) {
    t41 = <div className="mt-4 flex flex-wrap items-center gap-2" aria-live="polite">{t39}{t40}</div>;
    $[123] = t39;
    $[124] = t40;
    $[125] = t41;
  } else {
    t41 = $[125];
  }
  let t42;
  if ($[126] !== t16 || $[127] !== t17 || $[128] !== t21 || $[129] !== t38 || $[130] !== t41) {
    t42 = <section className={t16} aria-labelledby="inspiration-filter-title">{t17}{t21}{t38}{t41}</section>;
    $[126] = t16;
    $[127] = t17;
    $[128] = t21;
    $[129] = t38;
    $[130] = t41;
    $[131] = t42;
  } else {
    t42 = $[131];
  }
  return t42;
}
function FacetPopover(t0) {
  const $ = _c(44);
  const {
    facet,
    selectedIds,
    lockedIds,
    hiddenIds: t1,
    hasClearableSelection,
    onToggle,
    onClear
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const hiddenIds = t2;
  const t3 = selectedIds.length > 0 ? "secondary" : "outline";
  let t4;
  if ($[2] !== facet.icon) {
    t4 = <facet.icon data-icon="inline-start" />;
    $[2] = facet.icon;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== selectedIds.length) {
    t5 = selectedIds.length > 0 && <Badge variant="default">{selectedIds.length}</Badge>;
    $[4] = selectedIds.length;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ChevronDown data-icon="inline-end" />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== facet.label || $[8] !== t3 || $[9] !== t4 || $[10] !== t5) {
    t7 = <PopoverTrigger asChild={true}><Button type="button" variant={t3} className="cursor-pointer rounded-full">{t4}{facet.label}{t5}{t6}</Button></PopoverTrigger>;
    $[7] = facet.label;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== facet.icon) {
    t8 = <facet.icon className="size-4 shrink-0 text-gray-500" aria-hidden="true" />;
    $[12] = facet.icon;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== facet.mobileLabel || $[15] !== t8) {
    t9 = <p className="inline-flex min-w-0 items-center gap-2 font-bold text-gray-950">{t8}{facet.mobileLabel}</p>;
    $[14] = facet.mobileLabel;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = !hasClearableSelection && "invisible";
  let t11;
  if ($[17] !== t10) {
    t11 = cn("shrink-0 cursor-pointer text-gray-500", t10);
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = !hasClearableSelection;
  const t13 = hasClearableSelection ? undefined : -1;
  let t14;
  if ($[19] !== onClear || $[20] !== t11 || $[21] !== t12 || $[22] !== t13) {
    t14 = <Button type="button" variant="ghost" size="sm" className={t11} aria-hidden={t12} tabIndex={t13} onClick={onClear}>Clear</Button>;
    $[19] = onClear;
    $[20] = t11;
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t14 || $[25] !== t9) {
    t15 = <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">{t9}{t14}</div>;
    $[24] = t14;
    $[25] = t9;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const t16 = facet.options.length > 7 ? "h-72" : "h-auto";
  let t17;
  if ($[27] !== t16) {
    t17 = cn(t16);
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== facet || $[30] !== hiddenIds || $[31] !== lockedIds || $[32] !== onToggle || $[33] !== selectedIds) {
    t18 = <div className="p-2"><FilterOptionList facet={facet} selectedIds={selectedIds} lockedIds={lockedIds} hiddenIds={hiddenIds} idPrefix="desktop" onToggle={onToggle} /></div>;
    $[29] = facet;
    $[30] = hiddenIds;
    $[31] = lockedIds;
    $[32] = onToggle;
    $[33] = selectedIds;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t17 || $[36] !== t18) {
    t19 = <ScrollArea className={t17}>{t18}</ScrollArea>;
    $[35] = t17;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== t15 || $[39] !== t19) {
    t20 = <PopoverContent align="start" className="w-80 p-0">{t15}{t19}</PopoverContent>;
    $[38] = t15;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t20 || $[42] !== t7) {
    t21 = <Popover>{t7}{t20}</Popover>;
    $[41] = t20;
    $[42] = t7;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  return t21;
}
function FilterOptionList(t0) {
  const $ = _c(21);
  const {
    facet,
    selectedIds,
    lockedIds: t1,
    hiddenIds: t2,
    idPrefix,
    showLegend: t3,
    onToggle
  } = t0;
  let t4;
  if ($[0] !== t1) {
    t4 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const lockedIds = t4;
  let t5;
  if ($[2] !== t2) {
    t5 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const hiddenIds = t5;
  const showLegend = t3 === undefined ? false : t3;
  let t6;
  if ($[4] !== facet.options || $[5] !== hiddenIds) {
    const groups = new Map();
    facet.options.filter(option => !hiddenIds.includes(option.id)).forEach(option_0 => {
      const group = option_0.groupLabel ?? "";
      groups.set(group, [...(groups.get(group) ?? []), option_0]);
    });
    t6 = Array.from(groups.entries());
    $[4] = facet.options;
    $[5] = hiddenIds;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const groupedOptions = t6;
  let t7;
  if ($[7] !== facet.icon || $[8] !== facet.mobileLabel || $[9] !== showLegend) {
    t7 = showLegend && <FieldLegend className="inline-flex items-center gap-2"><facet.icon className="size-4 shrink-0 text-gray-500" aria-hidden="true" />{facet.mobileLabel}</FieldLegend>;
    $[7] = facet.icon;
    $[8] = facet.mobileLabel;
    $[9] = showLegend;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== facet.key || $[12] !== groupedOptions || $[13] !== idPrefix || $[14] !== lockedIds || $[15] !== onToggle || $[16] !== selectedIds) {
    t8 = groupedOptions.map(t9 => {
      const [groupLabel, options] = t9;
      return <div key={groupLabel || facet.key} className="flex flex-col gap-1">{groupLabel && <p className="px-2 pt-2 text-sm font-bold text-gray-500">{groupLabel}</p>}<FieldGroup className="gap-0.5">{options.map(option_1 => {
            const checkboxId = `${idPrefix}-${facet.key}-${option_1.id}`;
            const locked = lockedIds.includes(option_1.id);
            const checked = locked || selectedIds.includes(option_1.id);
            return <Field key={option_1.id} orientation="horizontal" className={cn("rounded-lg px-2 py-2.5 transition-colors", locked ? "opacity-70" : "hover:bg-gray-50")}><Checkbox id={checkboxId} checked={checked} disabled={locked} className={cn(!locked && "cursor-pointer")} onCheckedChange={() => onToggle(option_1.id)} /><FieldLabel htmlFor={checkboxId} className={cn("text-sm font-medium", locked ? "inline-flex items-center gap-1.5" : "cursor-pointer")}>{option_1.label}{locked && <Lock className="size-3 shrink-0 text-gray-400" aria-label="Locked filter" />}</FieldLabel></Field>;
          })}</FieldGroup></div>;
    });
    $[11] = facet.key;
    $[12] = groupedOptions;
    $[13] = idPrefix;
    $[14] = lockedIds;
    $[15] = onToggle;
    $[16] = selectedIds;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = <FieldSet className="gap-3">{t7}{t8}</FieldSet>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}